import { hashObject } from '@fullstory/util/hash';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, fullstorySelectors, HeatmapsRequestActions } from '@fullstory/redux';
import { useNotifications } from '@fullstory/react-hooks';
import { UnnamedHeatmap } from '@fullstory/proto/fs/services/dx-objects';
import { generateRoutePath, HEATMAP_CREATE_WITH_HASH_ROUTE } from '@fullstory/routes';
import { useHistory } from 'react-router-dom';
import { createPath } from 'history';
import { int64 } from '@fullstory/proto/google/protobuf';
import { useCallback } from 'react';
import {
  DependentEventFilter,
  Device,
  EventFilter,
  OS,
  Platform,
  TimeRange,
  UrlCategory,
} from '@fullstory/proto/fs/api/segment';
import {
  HeatmapSettings,
  SegmentSettings,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { DEFAULT_SEGMENT } from '../utils';
import { useIntl } from 'react-intl';
import { RepresentativePage } from '@fullstory/proto/fs/services/search-settings';

const { getOrgId } = fullstorySelectors;

export type HeatmapRouteSettings = {
  pageDefId: number;
  url?: string;
  segmentSettings?: SegmentSettings;
  timeRange?: TimeRange;
  representativePage?: RepresentativePage | null;
  os?: OS;
  device?: Device;
  platform?: Platform;
};

export const useRouteToHeatmap = () => {
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();
  const orgId = useSelector(getOrgId);
  const history = useHistory();
  const notification = useNotifications();

  const createUnnamedHeatmap = useCallback(
    async (heatmap: UnnamedHeatmap) => {
      const hash = `${hashObject(heatmap)}`;
      await dispatch(HeatmapsRequestActions.createUnnamedHeatmap({ hash, heatmap }));
      return hash;
    },
    [dispatch],
  );

  const routeToHeatmap = useCallback(
    ({
      pageDefId,
      url,
      segmentSettings,
      timeRange,
      representativePage = null,
      os,
      device,
      platform,
    }: HeatmapRouteSettings) => {
      const eventFilter: EventFilter =
        pageDefId !== 0 || !url
          ? { visitedPage: { equals: { value: [int64.fromNumber(pageDefId)], spec: [] } } }
          : { visitedUrl: { equals: { value: [{ value: url, category: UrlCategory.URL_FULL }] } } };

      const dependentEventFilter: DependentEventFilter[] = [];
      if (os !== undefined && os !== OS.OS_INVALID) {
        dependentEventFilter.push({ os: { equals: { value: [os] } } });
      }
      if (platform !== undefined && platform !== Platform.PLATFORM_INVALID) {
        dependentEventFilter.push({ platform: { equals: { value: [platform] } } });
      }

      const newUnnamedHeatmap: UnnamedHeatmap = {
        heatmap: {
          id: '',
          name: '',
          description: '',
          heatmapEvent: {
            primary: eventFilter,
            deps: dependentEventFilter,
          },
          heatmapSettings: {
            segment: segmentSettings || DEFAULT_SEGMENT,
            device: device || Device.DEVICE_DESKTOP,
            timeRange: timeRange || { startTime: 'NOW/DAY-6DAY', endTime: 'NOW/DAY+1DAY' },
            heatmapType: HeatmapSettings.HeatmapType.HEATMAP_TYPE_CLICK,
            secondaryHeatmapType: HeatmapSettings.ClickType.CLICK_TYPE_ALL_CLICKS,
            conversionEvent: null,
            focusElementId: '',
            focusOnlyOnSelection: false,
            groupByElementProperty: '',
          },
          representativePage,
        },
      };

      createUnnamedHeatmap(newUnnamedHeatmap)
        .then(heatmapHash => {
          const pathname = generateRoutePath(HEATMAP_CREATE_WITH_HASH_ROUTE, {
            orgId,
            hash: heatmapHash,
          });
          history.push(createPath({ pathname, search: location.search }));
        })
        .catch(() => {
          notification?.error(
            intl.formatMessage({ defaultMessage: 'Failed to view heatmap.', id: 'kQoR8o' }),
          );
        });
    },
    [createUnnamedHeatmap, history, notification, orgId, intl],
  );

  return routeToHeatmap;
};
